import { GavelOutlined as GavelOutlinedIcon, MoneyOffRounded as MoneyOffRoundedIcon } from 'components/icons';
import ListCreateButton from 'components/buttons/ListCreateButton';
import Chip from 'components/@extended/Chip.tsx';
import StatusChip from 'components/@extended/StatusChip';
import DmsLink from 'components/dmslink/DmsLink';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftRowMenu } from 'components/drafts/DraftRecordActions';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import Loader from 'components/Loader';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap.tsx';
import EntityLinkList from 'components/misc/EntityLinkList.tsx';
import SupervisedAndResponsibleEmployees from 'components/misc/SupervisedAndResponsibleEmployees.tsx';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { getContractStatusChipType } from 'pages/contracts/util.tsx';
import { getBudgetInfo } from 'pages/invoices/util/invoiceUtil.tsx';
import type { CellContext } from '@tanstack/react-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { tableLoadingProps } from 'components/tables/list-table/tableLoadingProps';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import {
    CASE_VIEWS,
    ListViewsPageContext,
    SAVED_VIEW_PARAM_KEY,
    ViewSearchParamsContext,
} from 'components/tables/list-table/filter-bar/builtInViews';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import CopyToClipboardIcon, { REVEAL_ON_COPY_GROUP_HOVER_CLASS } from 'components/util/CopyToClipboard';
import HoursDisplay from 'components/util/HoursDisplay';
import LinkWithOrigin from 'components/util/LinkWithOrigin.tsx';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { FC, ReactNode, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { useStartMatterExportMutation } from 'store/api/exports';
import { useLazyGetMattersCountQuery, useLazyGetMattersWithFiltersQuery } from 'store/api/matters';
import { ClassifierData } from 'types/classifiers.ts';
import { EXPORT_CONFIG } from 'types/exports';
import { FeatureFlag } from 'types/features.ts';
import { ActiveCoverDto, MatterListItem, MatterStateType } from 'types/matters';
import { getClassifierText, t } from 'utils/getTranslations';
import MatterIndicators, { hasMatterAccess } from 'components/matters/MatterIndicators';
import { cn } from 'utils/cn';
import { useLastNameFirst } from 'utils/nameOrder';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useAsyncExport } from 'utils/useAsyncExport.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { MatterActionButtons } from './buttons/MatterActionButtons';
import CoverMarker from './CoverMarker';
import { MATTER_LIST_CLASSIFIERS, useMatterFilterConfig } from './useMatterFilterConfig';
import { ENTITY_LINK_CLASS, getLinks, getStatusChipType } from './util.tsx';

export { filterMatterStateTypes } from './useMatterFilterConfig';

/** The list's count, with the drafts it lists. */
const useLazyCaseCountQuery: typeof useLazyGetMattersCountQuery = (options) => {
    const [count, ...rest] = useLazyGetMattersCountQuery(options);
    return [useWithDrafts(count), ...rest];
};

type Props = {
    passiveFilter?: string;
    disableExport?: boolean;
    disableUrlState?: boolean;
    canRememberFilters?: boolean;
    view?: 'default' | 'party-group-referrals';
    dataTableClassName?: string;
};

const getMatterHref = (matter: MatterListItem) => `/matter/${matter.id}`;

const MattersTable: FC<Props> = ({
    passiveFilter = '',
    disableExport = false,
    disableUrlState = false,
    view = 'default',
    canRememberFilters = true,
    dataTableClassName,
}) => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: MATTER_LIST_CLASSIFIERS,
    });
    const [fetchCases, { data, currentData, isFetching, isLoading, isError }] = useLazyGetMattersWithFiltersQuery();
    const fetchData = useWithDrafts(fetchCases);
    const tableLoading = tableLoadingProps({ isLoading, isFetching, currentData });

    const hasPermissionToExportMatters = useHasPermissions(['EXPORT.MATTERS']);
    const isSortingEnabled = useIsFeatureEnabled(FeatureFlag.SORTING_INVOICES);
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const { isBusy: isExportDataFetching, triggerManualDownload } = useAsyncExport(useStartMatterExportMutation, {
        entityType: 'MATTER',
        entityName: t('matters.export.entity-name'),
        fileName: EXPORT_CONFIG.MATTER.fileName,
    });

    // Column customisation belongs to the case list itself. The group-referrals view renders this
    // table with a different column set under the same `columnConfigId`, so letting it persist a
    // hidden set or an order would reshuffle the case list's own columns.
    const isCaseList = view === 'default';
    const lastNameFirst = useLastNameFirst();
    // In a record's tab the case list shows the Cases views itself; its own page hands them over.
    const isNewDesign = useIsNewDesign();
    const listPage = useContext(ListViewsPageContext);
    const recordViews = isNewDesign && isCaseList && !disableUrlState && !listPage;
    const savedView = useSearchParams()[0].get(SAVED_VIEW_PARAM_KEY);
    const builtInView = CASE_VIEWS.find(({ id }) => id === savedView) ?? CASE_VIEWS[0];
    const navigateWithReturn = useNavigateWithReturnState();

    const columns = useMemo<DataTableColumnDef<MatterListItem>[]>(
        () =>
            getMatterTableColumns(
                classifiers,
                view,
                isSortingEnabled,
                isPartyNumberUsageEnabled,
                isCaseList,
                lastNameFirst
            ),
        [classifiers, view, isSortingEnabled, isPartyNumberUsageEnabled, isCaseList, lastNameFirst]
    );

    const isFilterHidden = view === 'party-group-referrals';

    const filterConfig = useMatterFilterConfig({ classifiers, passiveFilter, isFilterHidden });

    if (isLoadingClassifiers) {
        return <Loader />;
    }
    const list = (
        <ListTableWithFilters<MatterListItem>
            key={recordViews ? builtInView.id : undefined}
            getRowHref={getMatterHref}
            // Managed v8 engine for the weighted-width layout, with the full set of column
            // affordances: show/hide, drag to reorder, drag the edge to resize. All three persist
            // under `columnConfigId` — and the referrals view gets its own key, so the case list's
            // saved widths / hidden set / order are not replayed onto its different column set
            // (it has no picker of its own to undo them with).
            columnConfigId={isCaseList ? 'matters-list' : 'matters-referrals'}
            dataTableClassName={dataTableClassName}
            enableColumnPicker={isCaseList}
            enableColumnReorder={isCaseList}
            enableColumnResize={isCaseList}
            scrollRestorationId="matters-list"
            columns={columns}
            // Hide the previous filters' rows while a new combination loads, so the
            // row skeleton replaces the list instead of trailing below stale rows.
            data={tableLoading.isLoading ? undefined : data}
            {...tableLoading}
            pluralTextId="matters.matters"
            singularTextId="matters.matter"
            isError={isError}
            noEntriesId={isCaseList ? 'matters.empty.title' : 'matters.no-matters-found'}
            noEntriesBodyId={isCaseList ? 'matters.empty.body' : undefined}
            noEntriesIcon={GavelOutlinedIcon}
            // Duplicates the page header's action in place, so an empty list is actionable without
            // hunting for a button whose position moves with permissions and breakpoint.
            noEntriesAction={
                isCaseList ? (
                    <ListCreateButton
                        permissions={['CREATE.MATTERS', 'CREATE.MATTERS.CLIENTS']}
                        variant="contained"
                        color="secondary"
                        onClick={() => navigateWithReturn('/matter/new')}
                        labelId="matters.add-new-matter"
                        newLabelId="matters.new-case"
                    />
                ) : null
            }
            fetchData={fetchData}
            fetchCount={useLazyCaseCountQuery}
            filterConfig={filterConfig}
            rowsPerPage={50}
            disableUrlState={disableUrlState}
            exportHookResults={
                disableExport || !hasPermissionToExportMatters
                    ? undefined
                    : [
                          {
                              labelKey: 'export',
                              isBusy: isExportDataFetching,
                              triggerManualDownload,
                          },
                      ]
            }
            canRememberFilters={canRememberFilters}
        />
    );
    return recordViews ? (
        <ListViewsPageContext.Provider value={{ listKey: 'matters', views: CASE_VIEWS, activeId: builtInView.id }}>
            <ViewSearchParamsContext.Provider value={builtInView.searchParams}>{list}</ViewSearchParamsContext.Provider>
        </ListViewsPageContext.Provider>
    ) : (
        list
    );
};

export default MattersTable;

export function getMatterTableColumns<T extends MatterListItem>(
    classifiers: Record<string, ClassifierData>,
    view: Props['view'] = 'default',
    isSortingEnabled: boolean,
    _isPartyNumberUsageEnabled: boolean,
    // The case list is the only view with a column picker, so it is the only one that gets the
    // hidden-by-default extras below; the others would render them as unhideable columns.
    isCaseList = false,
    lastNameFirst = false
): DataTableColumnDef<T>[] {
    type MatterCell<V = unknown> = CellContext<MatterListItem, V>;
    // A draft has no time, expenses or invoices yet.
    const orDraftDash =
        (cell: (context: MatterCell) => ReactNode) =>
        (context: MatterCell): ReactNode =>
            context.row.original.isDraft ? '—' : cell(context);
    const columns: DataTableColumnDef<MatterListItem>[] = [
        ...(view === 'party-group-referrals'
            ? [
                  {
                      header: t('matters.recommended-by'),
                      enableGrouping: false,
                      enableSorting: false,
                      meta: {
                          widthWeight: 2,
                          minPx: 220,
                      },
                      cell: ({ row }: MatterCell) => {
                          const referrals = row.original.referrals ?? [];
                          const referringCompanies = referrals
                              .map(({ companyParty }) => companyParty)
                              .filter((item) => item !== undefined);
                          const referringPersons = referrals
                              .map(({ personParty }) => personParty)
                              .filter((item) => item !== undefined);
                          return (
                              <div className="flex flex-col">
                                  {referringCompanies.length ? (
                                      <p className="text-body1 text-fg-secondary">
                                          <FormattedMessage
                                              id="matters.referring-companies"
                                              values={{
                                                  count: referringCompanies.length,
                                                  contacts: (
                                                      <span key="contacts" className="text-fg">
                                                          {getLinks('contact', referringCompanies)}
                                                      </span>
                                                  ),
                                              }}
                                          />
                                      </p>
                                  ) : null}
                                  {referringPersons.length ? (
                                      <p className="text-body1 text-fg-secondary">
                                          <FormattedMessage
                                              id="matters.referring-persons"
                                              values={{
                                                  count: referringPersons.length,
                                                  contacts: (
                                                      <span key="contacts" className="text-fg">
                                                          {getLinks('contact', referringPersons)}
                                                      </span>
                                                  ),
                                              }}
                                          />
                                      </p>
                                  ) : null}
                              </div>
                          );
                      },
                  },
              ]
            : []),
        {
            header: t('matters.name-in-column-header'),
            id: 'fullName',
            accessorKey: 'fullName',
            enableGrouping: false,
            enableSorting: !!isSortingEnabled,
            meta: {
                sortAccessor: 'caseNumber',
                // Measured at desktop widths: the name column renders ~1.35x the other (near-equal)
                // columns, not dominant.
                widthWeight: 1.35,
                minPx: 240,
            },
            cell: ({ row }: MatterCell) => <MatterNameCell matter={row.original} classifiers={classifiers} />,
        },
        {
            header: t('matters.service-agreement-column'),
            id: 'serviceAgreementContract',
            enableGrouping: false,
            // The contract lives behind a to-one FK the list query does not sort on.
            enableSorting: false,
            meta: {
                widthWeight: 1.2,
                minPx: 200,
            },
            cell: ({ row }: MatterCell) => {
                const { serviceAgreementContract } = row.original;
                if (!serviceAgreementContract) {
                    return null;
                }
                const { id, contractName, tenantCompanyName, contractSignedAt, contractStatusTypeCode } =
                    serviceAgreementContract;
                return (
                    <div className="flex min-w-0 flex-col items-start gap-1">
                        <RecordLink
                            withOrigin
                            to={`/contract/${id}`}
                            className={cn(ENTITY_LINK_CLASS, 'line-clamp-2 min-w-0 font-semibold')}
                            label={contractName ?? '-'}
                        />
                        <span className="text-caption text-fg-secondary">{tenantCompanyName ?? '-'}</span>
                        {contractSignedAt && <DateDefaultNowrap dateValue={contractSignedAt} />}
                        <StatusChip
                            type={getContractStatusChipType(contractStatusTypeCode)}
                            label={getClassifierText(classifiers['CONTRACT_STATUS_TYPE'], contractStatusTypeCode ?? '')}
                        />
                    </div>
                );
            },
        },
        {
            header: t('matters.opening-date'),
            id: 'openedDate',
            accessorKey: 'openedDate',
            enableGrouping: false,
            enableSorting: !!isSortingEnabled,
            meta: {
                widthWeight: 1,
                minPx: 120,
            },
            cell: ({ getValue }) => {
                const value = getValue<MatterListItem['openedDate']>();
                return value && <DateDefaultNowrap dateValue={value} />;
            },
        },
        {
            header: t('matters.responsible-persons'),
            accessorKey: 'responsibleEmployee',
            enableGrouping: false,
            enableSorting: false,
            meta: {
                // Room for a cover marker after the person it stands in for.
                widthWeight: 2,
                minPx: 300,
            },
            cell: ({ row }: MatterCell) => {
                const { responsibleEmployee, supervisedByEmployee, activeCovers } = row.original;
                const marker = (role: ActiveCoverDto['role']) => {
                    const cover = activeCovers?.find((c) => c.role === role);
                    return cover ? <CoverMarker cover={cover} /> : null;
                };

                return (
                    <SupervisedAndResponsibleEmployees
                        supervisedByEmployees={supervisedByEmployee}
                        responsibleEmployees={responsibleEmployee}
                        supervisedSuffix={marker('SUPERVISING_PARTNER')}
                        responsibleSuffix={marker('RESPONSIBLE_LAWYER')}
                    />
                );
            },
        },
        // The two halves of "Responsible persons" as separately pickable, sortable columns, rendering
        // the bare name where the combined column prefixes "SP:" / "RL:". Case list only — it is the
        // view with a column picker, so elsewhere they would be unhideable duplicates.
        ...(isCaseList
            ? [
                  {
                      header: t('matters.supervising-partner'),
                      id: 'supervisingPartner',
                      // A column is sortable only when it has an accessor; the value itself is unused
                      // (the cell overrides rendering). sortAccessor follows the displayed name order,
                      // which EmployeeName flips under LAST_NAME_FIRST.
                      accessorKey: 'supervisedByEmployee',
                      enableGrouping: false,
                      enableSorting: !!isSortingEnabled,
                      meta: {
                          sortAccessor: lastNameFirst
                              ? 'supervisedByEmployee.lastName'
                              : 'supervisedByEmployee.firstName',
                          widthWeight: 1,
                          minPx: 140,
                          defaultHidden: true,
                      },
                      cell: ({ row }: MatterCell) => {
                          const { supervisedByEmployee } = row.original;
                          return (
                              <EntityLinkList
                                  domain="employee"
                                  items={supervisedByEmployee ? [supervisedByEmployee] : undefined}
                              />
                          );
                      },
                  },
                  {
                      header: t('matters.responsible-lawyer'),
                      id: 'responsibleLawyer',
                      accessorKey: 'responsibleEmployee',
                      enableGrouping: false,
                      enableSorting: !!isSortingEnabled,
                      meta: {
                          sortAccessor: lastNameFirst
                              ? 'responsibleEmployee.lastName'
                              : 'responsibleEmployee.firstName',
                          widthWeight: 1,
                          minPx: 140,
                          defaultHidden: true,
                      },
                      cell: ({ row }: MatterCell) => {
                          const { responsibleEmployee } = row.original;
                          return (
                              <EntityLinkList
                                  domain="employee"
                                  items={responsibleEmployee ? [responsibleEmployee] : undefined}
                              />
                          );
                      },
                  },
              ]
            : []),
        {
            header: t('matters.office'),
            id: 'handledByOffice',
            accessorKey: 'handledByOffice',
            enableGrouping: false,
            enableSorting: !!isSortingEnabled,
            meta: {
                sortAccessor: 'handledByOffice.name',
                widthWeight: 1,
                minPx: 140,
            },
            cell: ({ getValue }) => {
                const value = getValue<MatterListItem['handledByOffice']>();
                return value ? (
                    <RecordLink
                        withOrigin
                        className={ENTITY_LINK_CLASS}
                        to={`/office/${value.id}`}
                        label={`${value.name ?? '-'}${officeInactiveSuffix(value) ?? ''}`}
                    />
                ) : null;
            },
        },
        ...(view === 'default'
            ? [
                  {
                      header: t('matters.uninvoiced-hours-total'),
                      id: 'matterStats.totalBilledTimeInHours',
                      enableGrouping: false,
                      enableSorting: false,
                      meta: {
                          widthWeight: 1,
                          minPx: 130,
                      },
                      cell: orDraftDash(({ row }: MatterCell) => {
                          const { matterStats } = row.original;
                          const uninvoicedHours = matterStats?.uninvoicedBilledTimeInHours || 0;
                          const totalHours = matterStats?.totalBilledTimeInHours || 0;
                          return matterStats ? (
                              <div className="flex flex-col">
                                  <p className="text-body1">
                                      <HoursDisplay value={uninvoicedHours} />
                                  </p>
                                  <span className="text-caption text-fg-secondary">
                                      <HoursDisplay value={totalHours} />
                                  </span>
                              </div>
                          ) : null;
                      }),
                  },
                  {
                      header: t('stats.uninvoiced-expenses'),
                      id: 'currencyDetails.uninvoicedExpensesInOriginalCurrency',
                      enableGrouping: false,
                      enableSorting: false,
                      meta: {
                          widthWeight: 1,
                          minPx: 140,
                      },
                      cell: orDraftDash(({ row }: MatterCell) => {
                          const { currencyDetails } = row.original;
                          return currencyDetails ? (
                              <div className="flex flex-col">
                                  <p className="text-body1">
                                      <MoneyDisplay
                                          amount={currencyDetails?.uninvoicedExpensesInOriginalCurrency ?? 0}
                                          currency={currencyDetails?.originalCurrency ?? ''}
                                      />
                                  </p>
                                  <span className="text-caption text-fg-secondary">
                                      <MoneyDisplay
                                          amount={currencyDetails?.uninvoicedExpensesInBaseCurrency ?? 0}
                                          currency={currencyDetails?.baseCurrency ?? ''}
                                      />
                                  </span>
                              </div>
                          ) : null;
                      }),
                  },
                  {
                      header: t('matters.uninvoiced-amount-total'),
                      id: 'currencyDetails.uninvoicedAmountInOriginalCurrency',
                      enableGrouping: false,
                      enableSorting: false,
                      meta: {
                          widthWeight: 1,
                          minPx: 140,
                      },
                      cell: orDraftDash(({ row }: MatterCell) => {
                          const { currencyDetails } = row.original;
                          const amountOriginalCurr =
                              (currencyDetails?.uninvoicedAmountInOriginalCurrency ?? 0) +
                              (currencyDetails?.uninvoicedExpensesInOriginalCurrency ?? 0);
                          const amountBaseCurr =
                              (currencyDetails?.uninvoicedAmountInBaseCurrency ?? 0) +
                              (currencyDetails?.uninvoicedExpensesInBaseCurrency ?? 0);
                          const currency = currencyDetails?.originalCurrency;
                          const baseCurrency = currencyDetails?.baseCurrency;

                          return currencyDetails ? (
                              <div className="flex flex-col">
                                  <p className="text-body1">
                                      <MoneyDisplay amount={amountOriginalCurr} currency={currency} />
                                  </p>
                                  <span className="text-caption text-fg-secondary">
                                      <MoneyDisplay amount={amountBaseCurr} currency={baseCurrency} />
                                  </span>
                              </div>
                          ) : null;
                      }),
                  },
                  {
                      header: t('stats.total-amount'),
                      id: 'currencyDetails.totalAmountInOriginalCurrency',
                      enableGrouping: false,
                      enableSorting: false,
                      meta: {
                          widthWeight: 1,
                          minPx: 140,
                      },
                      cell: orDraftDash(({ row }: MatterCell) => {
                          const { currencyDetails } = row.original;
                          const amountOriginalCurr =
                              (currencyDetails?.totalAmountInOriginalCurrency ?? 0) +
                              (currencyDetails?.totalExpensesInOriginalCurrency ?? 0);
                          const amountBaseCurr =
                              (currencyDetails?.totalAmountInBaseCurrency ?? 0) +
                              (currencyDetails?.totalExpensesInBaseCurrency ?? 0);
                          const currency = currencyDetails?.originalCurrency;
                          const baseCurrency = currencyDetails?.baseCurrency;

                          return currencyDetails ? (
                              <div className="flex flex-col">
                                  <p className="text-body1">
                                      <MoneyDisplay amount={amountOriginalCurr} currency={currency} />
                                  </p>
                                  <span className="text-caption text-fg-secondary">
                                      <MoneyDisplay amount={amountBaseCurr} currency={baseCurrency} />
                                  </span>
                              </div>
                          ) : null;
                      }),
                  },
              ]
            : []),
        // Opt-in extras mirroring the billable-clients list, hidden until the user picks them in the
        // column menu. Only the case list opts in: the other consumer (the project sub-list) reads
        // matters off the project payload, which resolves none of these fields.
        ...(isCaseList
            ? [
                  {
                      header: t('billable-clients.last-invoice'),
                      id: 'lastInvoice',
                      enableGrouping: false,
                      enableSorting: false,
                      meta: {
                          widthWeight: 1,
                          minPx: 140,
                          defaultHidden: true,
                      },
                      cell: ({ row }: MatterCell) => {
                          const { lastInvoice } = row.original;
                          if (!lastInvoice) {
                              return null;
                          }
                          return (
                              <div className="flex min-w-0 flex-col items-start gap-1">
                                  <RecordLink
                                      withOrigin
                                      className={ENTITY_LINK_CLASS}
                                      to={`/invoice/${lastInvoice.id}`}
                                      label={lastInvoice.invoiceNumber}
                                  />
                                  <StatusChip
                                      type="inactive"
                                      label={getClassifierText(
                                          classifiers['INVOICE_STATUS_TYPE'],
                                          lastInvoice.invoiceStatusTypeCode
                                      )}
                                  />
                                  <DateDefaultNowrap dateValue={lastInvoice.invoiceDate} />
                              </div>
                          );
                      },
                  },
                  {
                      header: t('invoices.cap-fixed-fee'),
                      id: 'capFixedFee',
                      enableGrouping: false,
                      enableSorting: false,
                      meta: {
                          widthWeight: 1,
                          minPx: 150,
                          defaultHidden: true,
                      },
                      cell: ({ row }: MatterCell) => {
                          const budget = getBudgetInfo(row.original);
                          // getBudgetInfo emits a label and an amount as siblings; a block wrapper
                          // keeps them in inline flow (so they can wrap) instead of becoming two
                          // items of the cell's flex box, which clips at the column edge.
                          return budget ? <div className="min-w-0">{budget}</div> : null;
                      },
                  },
                  {
                      header: t('billable-clients.last-timecard'),
                      id: 'latestTimecardDate',
                      enableGrouping: false,
                      enableSorting: false,
                      meta: {
                          widthWeight: 1,
                          minPx: 110,
                          defaultHidden: true,
                      },
                      cell: ({ row }: MatterCell) => {
                          const latestTimecardDate = row.original.matterStats?.latestTimecardDate;
                          return latestTimecardDate ? <DateDefaultNowrap dateValue={latestTimecardDate} /> : null;
                      },
                  },
                  {
                      header: t('billable-clients.last-exp'),
                      id: 'latestExpenseDate',
                      enableGrouping: false,
                      enableSorting: false,
                      meta: {
                          widthWeight: 1,
                          minPx: 110,
                          defaultHidden: true,
                      },
                      cell: ({ row }: MatterCell) => {
                          const { latestExpenseDate } = row.original;
                          return latestExpenseDate ? <DateDefaultNowrap dateValue={latestExpenseDate} /> : null;
                      },
                  },
                  {
                      header: t('billable-clients.next-invoice-interval'),
                      id: 'nextInvoicingDate',
                      enableGrouping: false,
                      enableSorting: false,
                      meta: {
                          widthWeight: 1,
                          minPx: 130,
                          defaultHidden: true,
                      },
                      cell: ({ row }: MatterCell) => {
                          const { nextInvoicingDate, invoicingIntervalTypeCode } = row.original;
                          if (!nextInvoicingDate && !invoicingIntervalTypeCode) {
                              return null;
                          }
                          return (
                              <div className="flex flex-col">
                                  {nextInvoicingDate ? <DateDefaultNowrap dateValue={nextInvoicingDate} /> : null}
                                  <span className="text-caption text-fg-secondary lowercase">
                                      {getClassifierText(
                                          classifiers['INVOICING_INTERVAL_TYPE'],
                                          invoicingIntervalTypeCode ?? undefined
                                      )}
                                  </span>
                              </div>
                          );
                      },
                  },
              ]
            : []),
        {
            header: '',
            id: 'actions',
            enableGrouping: false,
            enableSorting: false,
            meta: {
                width: 10,
                // Fixed column (single icon-button) — excluded from weighted flex, sized to its
                // measured width at desktop. Inert in the Projects sub-row table, which renders these
                // columns unmanaged, i.e. on the browser's auto table layout.
                fixedPx: 120,
            },
            cell: ({ cell }: MatterCell) => {
                const { id: matterId, matterStateTypeCode, visibility, isDraft } = cell.row.original;
                if (isDraft) {
                    return <DraftRowMenu draft={{ type: 'case', id: matterId }} />;
                }
                const isCaseClosed =
                    matterStateTypeCode === MatterStateType.COMPLETED ||
                    matterStateTypeCode === MatterStateType.OFFER_LOST ||
                    matterStateTypeCode === MatterStateType.OFFER_WON;
                const hasLimitedVisibility = visibility === 'LIMITED';

                return matterId ? (
                    <MatterActionButtons
                        matter={cell.row.original}
                        isDisabled={isCaseClosed}
                        hasLimitedVisibility={hasLimitedVisibility}
                    />
                ) : null;
            },
        },
    ];
    // The column array only reads MatterListItem fields, but DataTableColumnDef<T> references T in
    // invariant positions, so DataTableColumnDef<MatterListItem> is not assignable to
    // DataTableColumnDef<T> for T extends MatterListItem. The cast is sound.
    return columns as DataTableColumnDef<T>[];
}

/**
 * The Case cell: the case, its DMS folder, its client and its state. The new design sets the case number in
 * grey mono before a bold title, the folder as an icon chip beside it, the client on a muted line, and the
 * state as pills.
 */
function MatterNameCell({
    matter,
    classifiers,
}: {
    matter: MatterListItem;
    classifiers: Record<string, ClassifierData>;
}) {
    const isNewDesign = useIsNewDesign();
    const { id, fullName, caseNumber, name, clientParty, matterStateTypeCode, isNonBillable, isCurrentlyBlocked } =
        matter;
    const nonBillableChip = (className: string) =>
        isNonBillable ? (
            <Chip className={className} size="small" label={<MoneyOffRoundedIcon />} type={'partyType'} />
        ) : null;
    const statusChip = (className?: string) => (
        <StatusChip
            className={className}
            type={getStatusChipType(matterStateTypeCode)}
            label={getClassifierText(classifiers['MATTER_STATE_TYPE'], matterStateTypeCode)}
        />
    );

    // The name exactly as the cell shows it: the case number, then the title.
    const copyNameIcon = fullName ? (
        <CopyToClipboardIcon
            text={fullName}
            className={cn('-my-0.5 ml-0 shrink-0', REVEAL_ON_COPY_GROUP_HOVER_CLASS)}
        />
    ) : null;

    if (isNewDesign) {
        return (
            <div className="group/copy flex w-full min-w-0 flex-col gap-1">
                <div className="text-foreground flex min-w-0 items-start gap-1.5">
                    <LinkWithOrigin
                        to={`/matter/${id}`}
                        title={fullName}
                        className={cn(ENTITY_LINK_CLASS, 'line-clamp-2 min-w-0 text-sm leading-[1.35] font-semibold')}
                    >
                        {caseNumber ? (
                            <>
                                <span className="text-grey-600 text-meta font-mono font-medium">{caseNumber}</span>{' '}
                                {name}
                            </>
                        ) : (
                            (fullName ?? '-')
                        )}
                    </LinkWithOrigin>
                    {copyNameIcon}
                    {matter.dmsLink && !matter.isDraft ? <DmsLink link={matter.dmsLink} variant="icon-chip" /> : null}
                </div>
                {clientParty?.id ? (
                    <PartyName
                        party={clientParty}
                        isLink
                        useLinkWithOrigin
                        variant="list"
                        showPartyNumber
                        secondaryLine
                        // The glyph stays beside a long name as it wraps.
                        className="flex-nowrap items-start whitespace-normal [&>span:first-child]:mt-0.5"
                    />
                ) : null}
                <LinkWithOrigin className="entity-link w-fit no-underline" to={`/matter/${id}`}>
                    <div className="flex items-center gap-1.5">
                        {matter.isDraft ? <DraftChip className="cursor-pointer" /> : null}
                        <MatterIndicators
                            isConfidential={matter.isConfidential}
                            hasAccess={hasMatterAccess(matter)}
                            isBlocked={isCurrentlyBlocked}
                            variant="pill"
                            className="cursor-pointer"
                        />
                        {nonBillableChip('cursor-pointer')}
                        {statusChip('cursor-pointer')}
                    </div>
                </LinkWithOrigin>
            </div>
        );
    }

    return (
        <div className="group/copy flex w-full min-w-0 flex-col">
            <div className="flex min-w-0 items-center gap-2">
                <LinkWithOrigin
                    to={`/matter/${id}`}
                    className={cn(ENTITY_LINK_CLASS, 'line-clamp-2 min-w-0 font-semibold')}
                >
                    {fullName ?? '-'}
                </LinkWithOrigin>
                {copyNameIcon}
                {matter.dmsLink && !matter.isDraft && (
                    <div className="shrink-0 self-start">
                        <DmsLink link={matter.dmsLink} isIconDisplayed={true} />
                    </div>
                )}
            </div>
            {clientParty?.id ? (
                <PartyName
                    party={clientParty}
                    isLink
                    useLinkWithOrigin
                    variant="list"
                    showPartyNumber
                    className="whitespace-normal"
                />
            ) : null}
            <LinkWithOrigin className="entity-link w-fit no-underline" to={`/matter/${id}`}>
                <div className="flex gap-1">
                    {matter.isDraft ? <DraftChip className="mt-1 cursor-pointer" /> : null}
                    <MatterIndicators
                        isConfidential={matter.isConfidential}
                        hasAccess={hasMatterAccess(matter)}
                        isBlocked={isCurrentlyBlocked}
                        variant="chip"
                        className="mt-[4px] cursor-pointer"
                    />
                    {nonBillableChip('mt-1 cursor-pointer')}
                    {statusChip('mt-1 cursor-pointer')}
                </div>
            </LinkWithOrigin>
        </div>
    );
}
